import React, { useEffect, useMemo, useState } from 'react';
import { InventoryApiService } from '../../Service/InventoryApiService';
import WarehouseOffCanvas from './WarehouseOffCanvas';
import "../Dynamic/WarehouseLocation.scss";
import "../../sass/responsive.scss";
import Spinner from '../Common/Spinner';
import { Tooltip } from '@mui/material';

interface StackDetailsProps {
  warehouseId: string;
  warehouseName: string;
}

const StackDetails: React.FC<StackDetailsProps> = ({ warehouseId, warehouseName}) => {

  const [stackDetails, setStackDetails] = useState<any>();
  const [selectedLocationId, setSelectedLocationId] = useState();
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [warehouseSummary, setWarehouseSummary] = useState({ totalPallets: 0, totalLocations: 0, occupiedLocations: 0, capacityPct: 0 });

  const warehouseMaxRowConfig: Record<string, number> = {
         'WDTS WH1': 80,
         'WDTS WH2': 76,
         'WDTS WH3': 100,
         'WDTS WH4': 100,
       };

  const fetchStackDetails = async () => {
    setIsLoading(true);
    console.log(warehouseName);
    try {
      const response = await InventoryApiService.fetchLocationsByWarehouseId(warehouseName);
      console.log("API response for stack details: ", response.data);
       const fullLayout = generateWarehouseLayout(response.data);
       setStackDetails(fullLayout);

       //warehouse summary dynamically from actual location data (Uses the same maxRow + STACKS_PER_ROW logic as generateWarehouseLayout.
       const apiData: any[] = response.data;
       const maxRowFromApi = apiData.filter(item => item.row !== null).length > 0
         ? Math.max(...apiData.filter(item => item.row !== null).map(item => Number(item.row)))
         : 0;
    
       const configuredMaxRow = warehouseMaxRowConfig[warehouseName] ?? 76;
       const effectiveMaxRow = Math.max(configuredMaxRow, maxRowFromApi);  // matches generateWarehouseLayout logic
       const totalLocations = effectiveMaxRow * 18 * 6;          // 18 = STACKS_PER_ROW (same constant)
       const totalPallets = apiData.reduce((sum, item) => sum + (item.palletCount || 0), 0);
       const occupiedLocations = apiData.filter(item => item.row !== null && (item.palletCount || 0) > 0).length;
       const capacityPct = totalLocations > 0 ? Math.round((totalPallets / totalLocations) * 100) : 0;
       setWarehouseSummary({ totalPallets, totalLocations, occupiedLocations, capacityPct });
    } catch(error) {
      console.log("error occurred while api calling " + error)
    } finally {
      setIsLoading(false);
    }
  }

  const generateWarehouseLayout = (apiData) => {
    const STACKS_PER_ROW = 18;

    // 1. Get max row from API (excluding staging areas)
    const maxRowFromApi = Math.max(
      ...apiData
        .filter(item => item.row !== null)
        .map(item => item.row),
      0
    );

  
    const configuredMaxRow = warehouseMaxRowConfig[warehouseName] ?? 76;
    const maxRow = Math.max(configuredMaxRow, maxRowFromApi);

    // 2. Create a map for fast lookup of API data
    const apiMap = new Map();
    apiData.forEach(item => {
      if (item.row !== null && item.stack !== null) {
        apiMap.set(`${item.row}-${item.stack}`, item);
      }
    });

    // 3. Generate full layout
    const layout = [];

    for (let row = 1; row <= maxRow; row++) {
      for (let stack = 1; stack <= STACKS_PER_ROW; stack++) {
        const key = `${row}-${stack}`;

        if (apiMap.has(key)) {
          layout.push(apiMap.get(key));
        } else {
          layout.push({
            locationId: null,
            row,
            stack,
            stagingArea: "",
            palletCount: 0,
            status: "EMPTY"
          });
        }
      }
    }

    // 4. Add staging areas separately (optional but recommended)
    apiData
      .filter(item => item.stagingArea !== null)
      .forEach(item => layout.push(item));

    return layout;
  };


  useEffect(() => {
    console.log("warehouse Id and " + warehouseId +  " warehouse Nae " + warehouseName);
    if (warehouseId) {
      setShowLocationDetails(false);
      fetchStackDetails();
    }
  }, [warehouseId]);

  // Organize data: odd rows on left, even rows on right
  const organizeWarehouseData = (data) => {
    const organized = {
      oddRows: {},
      evenRows: {},
      stagingAreas: []
    };

    data?.forEach(item => {
      if (item.stagingArea) {
        organized.stagingAreas.push(item);
      } else if (item.row % 2 === 1) {
        // Odd rows go to left side
        if (!organized.oddRows[item.row]) {
          organized.oddRows[item.row] = [];
        }
        organized.oddRows[item.row].push(item);
      } else {
        // Even rows go to right side
        if (!organized.evenRows[item.row]) {
          organized.evenRows[item.row] = [];
        }
        organized.evenRows[item.row].push(item);
      }
    });

    return organized;
  };

  const organized = organizeWarehouseData(stackDetails);
  
  const allRows = useMemo(() => {
    if (!Array.isArray(stackDetails)) return [];

    return [...new Set(
      stackDetails
        .filter(d => d.row !== undefined && d.row !== null)
        .map(d => Number(d.row))  // ensure numeric
    )].sort((a, b) => a - b);
  }, [stackDetails]);

  const oddRows = allRows.filter(r => r % 2 === 1);
  const evenRows = allRows.filter(r => r % 2 === 0);

 const getBackgroundColor = (palletCount: number) => {
    if (!palletCount || palletCount <= 0) return "#ffffff";

    if (palletCount > 6) return "#F43F5E";   // High
    if (palletCount === 6) return "#34D399"; // Medium / Safe

    switch (palletCount) {
      case 5:
        return "#eab308"; // Dark Yellow
      case 4:
        return "#facc15"; // Medium Yellow
      case 3:
        return "#fde047"; // Light Yellow
      case 2:
        return "#fef08a"; // Very Light Yellow
      case 1:
        return "#fef9c3"; // Faint Yellow
      default:
        return "#ffffff";
    }
  };


  const handleViewLocation = (locationId:any) => {
    setShowLocationDetails(true);
    setSelectedLocationId(locationId);
    window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  }

  const handleStagingLocationView = (stagingArea: string) => {
    handleViewLocation(stagingArea);
  };

  const renderStack = (item) => (
    <div
      key={`${item.row}-${item.stack}`}
      className="pallets-row-item d-inline-flex align-items-center justify-content-center position-relative"
      style={{
        backgroundColor: getBackgroundColor(item?.palletCount),
      }}
      onClick={() => handleViewLocation(item?.locationId)}
    >
      {item?.palletCount}
    </div>
  );

  const renderRowSection = (rowNumbers, rowsData, side) => (
    <div className={`pallets-side-section d-flex flex-column ${ side == 'left' ? 'left-side-section align-items-start align-items-xl-end' : 'right-side-section align-items-start'}`}>
      <div className={`column-space-divider ${side == 'left' ? 'left-column' : 'right-column'}`}>

        <div
        className="column-item"
        onClick={() =>
          handleStagingLocationView(
            `${side === 'left' ? 'Odd' : 'Even'} Aisle Staging Area - Low`
          )
        }
      >
        <p>{side === 'left' ? 'Odd' : 'Even'} Aisle Staging Area - Low</p>
      </div>

      <div
        className="column-item"
        onClick={() =>
          handleStagingLocationView(
            `${side === 'left' ? 'Odd' : 'Even'} Aisle Staging Area - Middle`
          )
        }
      >
        <p>{side === 'left' ? 'Odd' : 'Even'} Aisle Staging Area - Middle</p>
      </div>

      <div
        className="column-item"
        onClick={() =>
          handleStagingLocationView(
            `${side === 'left' ? 'Odd' : 'Even'} Aisle Staging Area - High`
          )
        }
      >
        <p>{side === 'left' ? 'Odd' : 'Even'} Aisle Staging Area - High</p>
      </div>

      </div>
      <div className={`pallet-staging-area ${side == 'left' ? 'left-staging' : ( warehouseName === 'WDTS WH3' ? "warehouse-3-right" : "right-staging")}`}
          onClick={
            side !== 'right' || warehouseName !== 'WDTS WH3'
              ? () => handleStagingLocationView(`Bay 1 Staging Area ${side == 'left' ? 'Odd' : 'Even'}`)
              : undefined
          }
      >
        {side == 'right' && warehouseName === 'WDTS WH3' ? (
          <>
            <div onClick={() => handleStagingLocationView("Ethanol Non-Bonded Space (Drum Storage)")}>
              Ethanol Non-Bonded Space (Drum Storage)
            </div>
            <div onClick={() => handleStagingLocationView("Finished Goods Non-Bonded Space")}>
              Finished Goods Non-Bonded Space
            </div>
          </>
        )
          :
          (<div style={{ flex: 1 }}>
            Bay 1 Staging Area {side == 'left' ? 'Odd' : 'Even'}
          </div>)}

      </div>
      {rowNumbers.map((rowNum) => (
        <div key={rowNum} className="pallets-row">
          {side === 'right' && (
            <div className='text-start row-number-wrapper'>
              <span>
               {rowNum}
              </span>
            </div>
          )}
          {side === 'left' && (<div className="pallets-row-number-wrapper">
            {rowsData[rowNum] && rowsData[rowNum].slice().reverse().map(item => renderStack(item))}
          </div>)}

          {side === 'right' && (<div className=" pallets-row-number-wrapper">
            {rowsData[rowNum] && rowsData[rowNum].map(item => renderStack(item))}
          </div>)}

          {side === 'left' && (
            <div className='text-end row-number-wrapper'>
              <span>
                {rowNum}
              </span>
            </div>
          )}
        </div>
      ))}
      {warehouseName !== 'WDTS WH4' && (
      <div
        className={`pallet-staging-area ${
          side === 'left' ? 'left-staging' : 'right-staging'
        }`}
        onClick={() =>
          handleStagingLocationView(
            `Flex Space - ${side === 'left' ? 'Left' : 'Right'}`
          )
        }
      >
        Flex Space - {side === 'left' ? 'Left' : 'Right'}
      </div>
    )}

    </div>
  );

  const getCapacityClass = (pct: number) => {
    if (pct >= 80) return 'capacity-high';
    if (pct >= 50) return 'capacity-medium';
    return 'capacity-value';
  };

  return (
    <>
    
      <div className="userTable h-100">
        <div className="container">

              {!isLoading && (
                <div className="warehouse-summary-bar justify-content-center">
                  <div className="summary-card">
                    <span className="summary-label">
                      PALLETS
                      <Tooltip arrow title={"Total pallets currently used in this warehouse"}>
                        <span className="summary-info-icon">i</span>
                      </Tooltip>
                    </span>
                    <span className="summary-value">
                      {warehouseSummary.totalPallets.toLocaleString()}
                    </span>
                  </div>

                  <div className="summary-card">
                    <span className="summary-label">
                      LOCATIONS
                      <Tooltip arrow title={"Total locations currently used in this warehouse"}>
                        <span className="summary-info-icon">i</span>
                      </Tooltip>                    
                      </span>
                    <span className="summary-value">
                      {warehouseSummary.totalLocations.toLocaleString()}
                    </span>
                  </div>

                  <div className="summary-card">
                    <span className="summary-label">
                      CAPACITY
                      <Tooltip arrow title={"Total utilized capacity of the selected warehouse."}>
                        <span className="summary-info-icon">i</span>
                      </Tooltip>                    
                      </span>
                    <span className={`summary-value ${getCapacityClass(warehouseSummary.capacityPct)}`}>
                      {warehouseSummary.capacityPct}%
                    </span>
                  </div>
                </div>
              )}

              {isLoading ? (<p className="d-flex justify-content-center align-items-center vh-100 gap-2">
                  Loading... <Spinner />
                </p>
                ): (<div className="row pallets-detail-inner">
                <div className="col-xl-6 mb-5 mb-xl-0">
                  <div className="d-flex justify-content-xl-end justify-content-center">
                    {renderRowSection(oddRows, organized.oddRows, 'left')}
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="d-flex justify-content-xl-start justify-content-center">
                    {renderRowSection(evenRows, organized.evenRows, 'right')}
                  </div>
                </div>
              </div>)}
        </div>

        {showLocationDetails && (<WarehouseOffCanvas type='stack' warehouseName={warehouseName} warehouseId={warehouseId} locationId={selectedLocationId}  onClose={() => setShowLocationDetails(false)}/>)}
      </div>
    </>
  );
};

export default StackDetails;